import React, { useState, useEffect, useRef, useMemo } from 'react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isToday, addDays, differenceInDays, parseISO, startOfDay } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../../supabaseClient';
import { processVideoUrl } from '../../utils/videoUtils';
import { SafeIcon } from '../../common/Shared';

// Helper to parse YYYY-MM-DD strings as local midnight (no UTC shift)
const parseLocalDate = (dateStr) => {
  if (!dateStr) return null;
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
};

// 1. Plan Header
export const PlanHeader = ({ plan, client, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [activationDate, setActivationDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  
  const [formData, setFormData] = useState({
    owner_name: client?.owner_name || '',
    dog_name: client?.dog_name || '',
    plan_name: plan?.plan_name || '',
    goals: plan?.goals || ''
  });

  useEffect(() => {
    setFormData({
      owner_name: client?.owner_name || '',
      dog_name: client?.dog_name || '',
      plan_name: plan?.plan_name || '',
      goals: plan?.goals || ''
    });
  }, [plan, client]);

  if (!plan || !client) return null;

  const startDate = parseLocalDate(plan.start_date);
  const endDate = parseLocalDate(plan.end_date);

  const handleActivatePlan = async () => {
    setIsSaving(true);
    try {
      // 1. Fetch tasks to calculate duration
      const { data: tasks } = await supabase
        .from('training_tasks')
        .select('id, week_number, day_number')
        .eq('plan_id', plan.id);

      if (!tasks || tasks.length === 0) {
        throw new Error("No tasks found in this plan to schedule.");
      }

      // 2. Calculate End Date
      const maxDays = tasks.reduce((max, t) => {
        const days = ((t.week_number - 1) * 7) + (t.day_number - 1);
        return Math.max(max, days);
      }, 0);
      
      const calculatedEnd = format(addDays(parseISO(activationDate), maxDays), 'yyyy-MM-dd');

      // 3. Update Plan Dates
      const { error: planError } = await supabase
        .from('training_plans')
        .update({
          start_date: activationDate,
          end_date: calculatedEnd
        })
        .eq('id', plan.id);

      if (planError) throw planError;

      // 4. Shift all tasks to the new date grid
      const [y, m, d] = activationDate.split('-').map(Number);
      const baseDate = new Date(y, m - 1, d);

      const taskUpdates = tasks.map(t => {
        const daysToAdd = ((t.week_number - 1) * 7) + (t.day_number - 1);
        const taskDate = format(addDays(baseDate, daysToAdd), 'yyyy-MM-dd');
        return supabase.from('training_tasks').update({ task_date: taskDate }).eq('id', t.id);
      });

      await Promise.all(taskUpdates);

      setIsActivating(false);
      onUpdate?.();
    } catch (err) {
      alert('Activation failed: ' + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const { error: clientError } = await supabase
        .from('training_clients')
        .update({
          owner_name: formData.owner_name.trim(),
          dog_name: formData.dog_name.trim()
        })
        .eq('id', client.id);

      if (clientError) throw clientError;

      const { error: planError } = await supabase
        .from('training_plans')
        .update({
          plan_name: formData.plan_name.trim(),
          goals: formData.goals.trim()
        })
        .eq('id', plan.id);

      if (planError) throw planError;

      setIsEditing(false);
      onUpdate?.();
    } catch (err) {
      alert('Failed to save: ' + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="bg-white shadow-sm border-b border-gray-100 p-4 md:p-6">
      <div className="max-w-4xl mx-auto">
        <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2 text-gray-500 text-sm font-medium">
                <SafeIcon name="User" className="w-4 h-4" />
                <span>{client.owner_name} & {client.dog_name}</span>
              </div>
              <button 
                onClick={() => setIsEditing(true)} 
                className="text-xs font-bold text-indigo-600 hover:text-indigo-800 flex items-center gap-1.5 bg-indigo-50 px-3 py-1.5 rounded-lg transition-all"
              >
                <SafeIcon name="Edit" className="w-3 h-3" /> Edit Profile
              </button>
            </div>
            
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{plan.plan_name}</h1>
            
            <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
              <div className="flex items-center gap-1">
                <SafeIcon name="Calendar" className="w-4 h-4" />
                <span className={!plan.start_date ? 'text-orange-600 font-bold' : ''}>
                  {startDate ? format(startDate, 'MMM d, yyyy') : 'TBD'} - {' '}
                  {endDate ? format(endDate, 'MMM d, yyyy') : 'TBD'}
                </span>
              </div>
            </div>

            {!plan.start_date && (
              <div className="bg-orange-50 border border-orange-100 p-4 rounded-2xl mb-4 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-100 text-orange-600 rounded-xl flex items-center justify-center shrink-0">
                    <SafeIcon name="Clock" />
                  </div>
                  <div>
                    <p className="text-sm font-black text-orange-900">Plan is currently inactive</p>
                    <p className="text-xs text-orange-700 font-medium">Set your start date to begin your training schedule.</p>
                  </div>
                </div>
                <button 
                  onClick={() => setIsActivating(true)}
                  className="bg-orange-600 text-white px-6 py-2.5 rounded-xl font-black text-xs shadow-lg hover:bg-orange-700 transition-all active:scale-95"
                >
                  Activate Schedule
                </button>
              </div>
            )}

            {plan.goals && (
              <div className="bg-blue-50 text-blue-800 p-4 rounded-2xl text-sm border border-blue-100">
                <span className="font-bold block mb-1 uppercase tracking-widest text-[10px] text-blue-500">My Training Goals</span>
                <p className="leading-relaxed">{plan.goals}</p>
              </div>
            )}
          </div>

          {plan.resource_url && (
            <a href={plan.resource_url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-xl font-bold transition-all w-full md:w-auto shadow-lg shadow-indigo-100" >
              <SafeIcon name="ExternalLink" className="w-4 h-4" /> Open Training Guide
            </a>
          )}
        </div>
      </div>

      {/* Activation Modal */}
      <AnimatePresence>
        {isActivating && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[10002]">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-md overflow-hidden"
              onClick={e => e.stopPropagation()}
            >
              <div className="p-8 border-b bg-gray-50 flex justify-between items-center">
                <div>
                  <h3 className="text-xl font-black text-gray-900">Activate Plan</h3>
                  <p className="text-xs text-gray-500 font-bold uppercase tracking-widest mt-1">Pick your official start date</p>
                </div>
                <button onClick={() => setIsActivating(false)} className="p-2 hover:bg-gray-200 rounded-full transition-colors"><SafeIcon name="X" /></button>
              </div>
              <div className="p-8 space-y-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">When would you like to start?</label>
                  <input 
                    type="date" 
                    value={activationDate} 
                    onChange={e => setActivationDate(e.target.value)}
                    className="w-full px-5 py-4 bg-gray-50 border-2 border-indigo-100 rounded-2xl font-black outline-none focus:border-indigo-500 transition-all"
                  />
                </div>
                <p className="text-xs text-gray-500 font-medium leading-relaxed">
                  Once activated, your training tasks will be assigned specific dates on your calendar. You can always shift the schedule later if you fall behind!
                </p>
              </div>
              <div className="p-8 pt-0">
                <button 
                  onClick={handleActivatePlan}
                  disabled={isSaving}
                  className="w-full bg-indigo-600 text-white py-4 rounded-2xl font-black shadow-xl shadow-indigo-100 hover:bg-indigo-700 transition-all flex items-center justify-center gap-2"
                >
                  {isSaving ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : 'Confirm & Start Training'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Edit Profile Modal */}
      <AnimatePresence>
        {isEditing && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[10001]">
            <motion.div initial={{opacity: 0,scale: 0.95}} animate={{opacity: 1,scale: 1}} exit={{opacity: 0,scale: 0.95}} className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-lg overflow-hidden" onClick={e=> e.stopPropagation()} >
              <div className="p-8 border-b flex justify-between items-center bg-gray-50">
                <div>
                  <h3 className="text-xl font-black text-gray-900">Edit My Profile</h3>
                  <p className="text-xs text-gray-500 font-bold uppercase tracking-widest mt-1">Update your info and goals</p>
                </div>
                <button onClick={()=> setIsEditing(false)} className="p-2 hover:bg-gray-200 rounded-full transition-colors"><SafeIcon name="X" /></button>
              </div>
              <div className="p-8 space-y-6 max-h-[70vh] overflow-y-auto">
                <div className="bg-orange-50 border border-orange-100 p-4 rounded-2xl flex items-start gap-3">
                  <div className="p-1.5 bg-orange-100 text-orange-600 rounded-lg shrink-0"><SafeIcon name="Info" className="w-4 h-4" /></div>
                  <div>
                    <h4 className="text-xs font-black text-orange-900 uppercase tracking-widest mb-1">Important Note</h4>
                    <p className="text-xs text-orange-800 font-medium leading-relaxed">Changing your <b>Name</b> or your <b>Dog's Name</b> will change the information you use to sign in to your portal.</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">My Name</label>
                    <input value={formData.owner_name} onChange={e=> setFormData({...formData,owner_name: e.target.value})} className="w-full px-4 py-3 bg-gray-50 border-2 border-transparent rounded-xl focus:border-indigo-500 outline-none font-bold" />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Dog's Name</label>
                    <input value={formData.dog_name} onChange={e=> setFormData({...formData,dog_name: e.target.value})} className="w-full px-4 py-3 bg-gray-50 border-2 border-transparent rounded-xl focus:border-indigo-500 outline-none font-bold" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Plan Name</label>
                  <input value={formData.plan_name} onChange={e=> setFormData({...formData,plan_name: e.target.value})} className="w-full px-4 py-3 bg-gray-50 border-2 border-transparent rounded-xl focus:border-indigo-500 outline-none font-bold" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">My Training Goals</label>
                  <textarea value={formData.goals} onChange={e=> setFormData({...formData,goals: e.target.value})} rows={4} className="w-full px-4 py-3 bg-gray-50 border-2 border-transparent rounded-xl focus:border-indigo-500 outline-none font-medium resize-none" />
                </div>
              </div>
              <div className="p-8 pt-4 border-t bg-gray-50">
                <div className="flex gap-4">
                  <button onClick={handleSave} disabled={isSaving} className="flex-1 bg-indigo-600 text-white py-4 rounded-2xl font-black shadow-xl shadow-indigo-100 hover:bg-indigo-700 transition-all flex items-center justify-center gap-2" >
                    {isSaving ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : 'Save Changes'}
                  </button>
                  <button onClick={()=> setIsEditing(false)} className="flex-1 bg-white border-2 border-gray-200 text-gray-500 py-4 rounded-2xl font-black hover:bg-gray-100 transition-all" > Cancel </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ... Rest of the file remains the same ...